import { Auth0Client } from "@auth0/auth0-spa-js";
import PropTypes from "prop-types";
import { createContext, useEffect, useReducer } from "react";
import { auth0Config } from "../config";
import { getUserData } from "../utils/userMetadata";
let auth0Client = null;
const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  isLoggedOut: false,
  user: null,
};
const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    isLoggedOut: true,
    user: null,
  }),
};
const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  platform: "Auth0",
  loginWithPopup: () => Promise.resolve(),
  loginWithRedirect: () => Promise.resolve(),
  checkAuthorization: () => Promise.resolve(false),
  logout: () => Promise.resolve(),
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  const checkPermission = async (token) => {
    const tokenData = JSON.parse(atob(token.split(".")[1]));
    const permissions = tokenData.permissions.includes("Dashboard");
    if (!permissions) {
      window.location.replace(
        `${process.env.REACT_APP_DEMO_URL}/?session=init`
      );
    }
  };

  const handleUserData = (user, token) => {
    localStorage.setItem("token", token);
    localStorage.setItem("email", user.email);
    const tokenParts = token.split(".");
    if (tokenParts[1]) {
      const tokenData = JSON.parse(atob(tokenParts[1]));
      localStorage.setItem(
        "exampleQuery",
        JSON.stringify(
          tokenData["https://dashboard-api.qtravel.ai/app_metadata"][
            "query_examples"
          ]
        )
      );
      localStorage.setItem(
        "qapiToken",
        tokenData["https://dashboard-api.qtravel.ai/app_metadata"]["qapi_token"]
      );
      localStorage.setItem(
        "partnerSlug",
        tokenData["https://dashboard-api.qtravel.ai/app_metadata"][
          "partner_slug"
        ]
      );
      localStorage.setItem(
        "countryCode",
        tokenData["https://dashboard-api.qtravel.ai/app_metadata"][
          "country_code"
        ]
      );
      localStorage.setItem(
        "languageCode",
        tokenData["https://dashboard-api.qtravel.ai/app_metadata"][
          "language_code"
        ]
      );
      localStorage.setItem(
        "currency",
        tokenData["https://dashboard-api.qtravel.ai/app_metadata"]["currency"]
      );
      localStorage.setItem(
        "plans",
        JSON.stringify(
          tokenData["https://dashboard-api.qtravel.ai/app_metadata"]["plans"]
        )
      );
      localStorage.setItem(
        "permissions",
        JSON.stringify(tokenData.permissions)
      );
      localStorage.setItem(
        "Privacy",
        JSON.stringify(
          tokenData["https://dashboard-api.qtravel.ai/user_metadata"]["Privacy"]
        )
      );
      localStorage.setItem(
        "Terms",
        JSON.stringify(
          tokenData["https://dashboard-api.qtravel.ai/user_metadata"]["Terms"]
        )
      );
      localStorage.setItem(
        "user_metadata",
        JSON.stringify(
          tokenData["https://dashboard-api.qtravel.ai/user_metadata"]
        )
      );
    }
  };

  const removeUserData = () => {
    localStorage.removeItem("qapiToken");
    localStorage.removeItem("partnerSlug");
    localStorage.removeItem("token");
    localStorage.removeItem("countryCode");
    localStorage.removeItem("languageCode");
    localStorage.removeItem("currency");
    localStorage.removeItem("permissions");
    localStorage.removeItem("exampleQuery");
    localStorage.removeItem("plans");
    localStorage.removeItem("email");
    localStorage.removeItem("Terms");
    localStorage.removeItem("Privacy");
    localStorage.removeItem("user_metadata");
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        auth0Client = new Auth0Client({
          redirect_uri: window.location.origin,
          ...auth0Config,
        });
        await auth0Client.checkSession({ ignoreCache: true });
        const isAuthenticated = await auth0Client.isAuthenticated();
        if (isAuthenticated) {
          const user = await auth0Client.getUser();
          const token = await auth0Client.getTokenSilently({
            ignoreCache: true,
          });
          handleUserData(user, token);
          checkPermission(token);
          // Here you should extract the complete user profile to make it
          // available in your entire app.
          // The auth state only provides basic information.
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated,
              user: {
                id: user.sub,
                avatar: user.picture,
                email: user.email,
                name: user.name,
                plan: "Premium",
              },
            },
          });
        } else {
          removeUserData();
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated,
              user: null,
            },
          });
        }
      } catch (err) {
        removeUserData();
        console.error(err);
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };
    initialize();
  }, []);
  const loginWithPopup = async (options) => {
    await auth0Client.loginWithPopup(options);
    const isAuthenticated = await auth0Client.isAuthenticated();
    if (isAuthenticated) {
      const user = await auth0Client.getUser();
      const token = await auth0Client.getTokenSilently();
      const UserData = await getUserData(token);
      handleUserData(user, token);
      checkPermission(token);
      // Here you should extract the complete user profile to make it available in your entire app.
      // The auth state only provides basic information.

      dispatch({
        type: "LOGIN",
        payload: {
          user: {
            id: user.sub,
            avatar: user.picture,
            email: user.email,
            name: user.name,
            phone_number: UserData.user_metadata.phone_number,
            plan: "Premium",
          },
        },
      });
    } else {
      removeUserData();
    }
  };
  const checkAuthorization = async (): Promise<boolean> => {
    try {
      await auth0Client.handleRedirectCallback();
      // eslint-disable-next-line no-empty
    } catch (e) {}
    const isAuthenticated = await auth0Client.isAuthenticated();
    if (isAuthenticated) {
      const user = await auth0Client.getUser();
      const token = await auth0Client.getTokenSilently();
      localStorage.setItem("token", token);
      const UserData = await getUserData(token);
      handleUserData(user, token);
      checkPermission(token);
      // Here you should extract the complete user profile to make it available in your entire app.
      // The auth state only provides basic information.

      dispatch({
        type: "LOGIN",
        payload: {
          user: {
            id: user.sub,
            avatar: user.picture,
            email: user.email,
            name: user.name,
            phone_number: UserData.user_metadata.phone_number,
            plan: "Premium",
          },
        },
      });
    } else {
      removeUserData();
    }
    return isAuthenticated;
  };
  const loginWithRedirect = async (options) => {
    await auth0Client.loginWithRedirect(options);
  };
  const logout = () => {
    removeUserData();
    auth0Client.logout();
    dispatch({
      type: "LOGOUT",
    });
  };
  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: "Auth0",
        loginWithPopup,
        loginWithRedirect,
        checkAuthorization,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
export default AuthContext;
